import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCheck, ListOrdered, MessageSquare, Printer, Save, TriangleAlert, X } from 'lucide-react';
import {
  useListExamsQuery,
  useListSubjectsQuery,
  useListExamGroupsQuery,
  useGetMarkSheetQuery,
  useSaveScoresMutation,
  useClearScoreMutation,
  useGetClassResultsQuery,
  useSaveRemarkMutation,
  getApiError,
  type ReportCard,
} from '@/store/api';
import { useAuthStore } from '@/store/auth.store';
import { PageLayout } from '@/components/ui/PageLayout';
import { Button } from '@/components/ui/Button';
import { Field, Select } from '@/components/ui/Field';
import { Modal } from '@/components/ui/Modal';
import { Mark } from '@/components/ui/Badge';
import { StatTile } from '@/components/ui/StatTile';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

type Draft = Record<string, { classScore: string; examScore: string }>;

/*
  ═════════════════════════════════════════════════════════════════════════════
  ENTERING MARKS

  This is a teacher with a stack of marked scripts and a column of names, so it
  is a grid with two number boxes per row and one Save — not a form per child.

  Why marks are held locally until Save
  ─────────────────────────────────────
  Saving on every keystroke means forty children × two columns of requests, and
  a half-typed "8" on its way to becoming "85" written to the database as an 8.
  The draft lives in this component and goes in one request, which is also what
  makes the unsaved-work warning possible.

  Why both columns are out of 100
  ───────────────────────────────
  Continuous assessment is written down out of 30 here and out of 40 there, and
  a grid where column three and column four have different denominators gets
  read wrong by somebody eventually. The teacher converts once, knowing what
  the denominator was, and everything after that compares like with like.

  Why the totals update as you type
  ─────────────────────────────────
  The weighting is the exam's and it is not always 30/70, so a teacher cannot
  check the arithmetic in their head. Showing the total live is how a mistyped
  digit gets noticed before it is saved rather than after a parent reads it.
  ═════════════════════════════════════════════════════════════════════════════
*/
export function MarkEntryPage() {
  const { examId = '' } = useParams();
  const institutionId = useAuthStore((s) => s.user?.institutionId ?? '');
  const navigate = useNavigate();

  const { data: exams = [] } = useListExamsQuery(institutionId, { skip: !institutionId });
  const { data: subjects = [] } = useListSubjectsQuery({ institutionId }, { skip: !institutionId });
  const { data: groups = [] } = useListExamGroupsQuery(institutionId, { skip: !institutionId });

  const exam = exams.find((e) => e._id === examId);
  const published = exam?.status === 'published';

  const [groupId, setGroupId] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [view, setView] = useState<'marks' | 'ranking'>('marks');
  const [draft, setDraft] = useState<Draft>({});

  /* The exam may be limited to some classes; never offer one it does not cover. */
  const openableGroups = useMemo(
    () => (exam && exam.groupIds.length > 0 ? groups.filter((g) => exam.groupIds.includes(g._id)) : groups),
    [groups, exam],
  );

  useEffect(() => {
    if (!groupId && openableGroups.length > 0) setGroupId(openableGroups[0]!._id);
  }, [openableGroups, groupId]);
  useEffect(() => {
    if (!subjectId && subjects.length > 0) setSubjectId(subjects[0]!._id);
  }, [subjects, subjectId]);

  const ready = Boolean(institutionId && examId && groupId && subjectId);

  const { data: sheet, isFetching } = useGetMarkSheetQuery(
    { institutionId, examId, groupId, subjectId },
    { skip: !ready },
  );
  const { data: ranking = [], isFetching: rankingLoading } = useGetClassResultsQuery(
    { institutionId, examId, groupId },
    { skip: !ready || view !== 'ranking' },
  );

  const [saveScores, saveState] = useSaveScoresMutation();
  const [clearScore, clearState] = useClearScoreMutation();
  const [saveRemark, remarkState] = useSaveRemarkMutation();

  /*
    Remarks are written here, on the ranking, because that is where the whole
    class is in front of you — the moment a head teacher is actually forming a
    view of each child rather than typing numbers for one subject.
  */
  const [remarkFor, setRemarkFor] = useState<ReportCard | null>(null);
  const [remarkDraft, setRemarkDraft] = useState({ classTeacherRemark: '', headTeacherRemark: '' });
  const role = useAuthStore((st) => st.user?.role);
  const isOffice = role === 'institution_admin' || role === 'super_admin';

  const openRemark = (card: ReportCard) => {
    setRemarkDraft({
      classTeacherRemark: card.classTeacherRemark,
      headTeacherRemark: card.headTeacherRemark,
    });
    setRemarkFor(card);
  };

  const submitRemark = async () => {
    if (!remarkFor) return;
    const result = await saveRemark({
      institutionId,
      examId,
      studentId: remarkFor.studentId,
      classTeacherRemark: remarkDraft.classTeacherRemark,
      /* Only the office may write this line, and sending it as a teacher is a
         403 — so it is not sent at all. */
      ...(isOffice ? { headTeacherRemark: remarkDraft.headTeacherRemark } : {}),
    });
    if (!('error' in result)) setRemarkFor(null);
  };

  /* A draft belongs to one class and one subject. Carrying it across either
     would write Class 1's marks onto Class 2. */
  useEffect(() => setDraft({}), [groupId, subjectId, examId]);

  const valueFor = (studentId: string, field: 'classScore' | 'examScore'): string => {
    const edited = draft[studentId]?.[field];
    if (edited !== undefined) return edited;
    const row = sheet?.rows.find((r) => r.studentId === studentId);
    const stored = row?.[field];
    return stored === null || stored === undefined ? '' : String(stored);
  };

  /* The ceiling is the exam's, so a teacher cannot type 45 into a column that
     tops out at 30 and discover it only when they press Save. */
  const classMax = sheet?.classMax ?? 100;
  const examMax = sheet?.examMax ?? 100;

  const setValue = (studentId: string, field: 'classScore' | 'examScore', raw: string) => {
    const cleaned = raw.replace(/[^\d]/g, '').slice(0, 3);
    const ceiling = field === 'classScore' ? classMax : examMax;
    if (cleaned !== '' && Number(cleaned) > ceiling) return;
    setDraft((d) => ({
      ...d,
      [studentId]: {
        classScore: field === 'classScore' ? cleaned : valueFor(studentId, 'classScore'),
        examScore: field === 'examScore' ? cleaned : valueFor(studentId, 'examScore'),
      },
    }));
  };

  /*
    Live, and it has to match what the API will store exactly — a preview that
    disagrees with the saved total is worse than no preview, because the
    teacher checks the one on screen.

      weight  — the marks are already scaled, so the total is their sum.
      percent — both are out of 100 and the weights combine them.
  */
  const totalFor = (studentId: string): number | null => {
    const c = valueFor(studentId, 'classScore');
    const e = valueFor(studentId, 'examScore');
    if (c === '' || e === '' || !exam) return null;
    return exam.markScale === 'weight'
      ? Number(c) + Number(e)
      : Math.round((Number(c) * exam.classScoreWeight + Number(e) * exam.examScoreWeight) / 100);
  };

  /* Only rows where BOTH boxes have a number. A child with one mark typed is
     mid-entry, and sending half of them would write a zero for the other. */
  const complete = Object.entries(draft).filter(
    ([, v]) => v.classScore !== '' && v.examScore !== '',
  );
  const partial = Object.values(draft).filter(
    (v) => (v.classScore === '') !== (v.examScore === ''),
  ).length;

  const save = async () => {
    if (complete.length === 0) return;
    const result = await saveScores({
      institutionId,
      examId,
      subjectId,
      scores: complete.map(([studentId, v]) => ({
        studentId,
        classScore: Number(v.classScore),
        examScore: Number(v.examScore),
      })),
    });
    if (!('error' in result)) setDraft({});
  };

  /* Closing a tab mid-column is how an afternoon's marking disappears. */
  useEffect(() => {
    if (complete.length === 0) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [complete.length]);

  const subject = subjects.find((s) => s._id === subjectId);

  return (
    <PageLayout
      title={exam?.name ?? 'Marks'}
      subtitle={
        !exam
          ? 'Loading the exam…'
          : exam.markScale === 'weight'
            ? `Class score out of ${exam.classScoreWeight}, exam out of ${exam.examScoreWeight} — the total is the two added`
            : `${exam.classScoreWeight}% class score · ${exam.examScoreWeight}% exam · both entered out of 100`
      }
      breadcrumbs={[{ label: 'Academics' }, { label: 'Exam results', href: '/exams' }, { label: 'Marks' }]}
      contained={false}
      actions={
        <div className="flex gap-2">
          {/* Printing is offered from the ranking, where the whole class is
              already in view, rather than from the mark grid where a teacher
              is still mid-column. */}
          {view === 'ranking' && (
            <Button
              variant="secondary"
              size="sm"
              disabled={!groupId}
              onClick={() => navigate(`/exams/${examId}/print?groupId=${groupId}`)}
            >
              <Printer size={13} className="mr-1" />
              Print report cards
            </Button>
          )}
          <Button variant="secondary" size="sm" onClick={() => navigate('/exams')}>
            <ArrowLeft size={13} className="mr-1" />
            All exams
          </Button>
        </div>
      }
      toolbar={
        <>
          <Select aria-label="Class" className="w-[170px]" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
            {openableGroups.map((g) => (
              <option key={g._id} value={g._id}>
                {g.name}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Subject"
            className="w-[190px]"
            value={subjectId}
            onChange={(e) => setSubjectId(e.target.value)}
            disabled={view === 'ranking'}
          >
            {subjects.map((s) => (
              <option key={s._id} value={s._id}>
                {s.name}
              </option>
            ))}
          </Select>

          <div className="flex items-center gap-0.5 rounded-md border border-border bg-muted/50 p-0.5">
            {(['marks', 'ranking'] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                aria-pressed={view === v}
                className={cn(
                  'rounded px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.05em] transition-colors',
                  view === v ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {v === 'marks' ? 'Enter marks' : 'Class ranking'}
              </button>
            ))}
          </div>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {published && (
          <div className="flex items-start gap-2 rounded-lg border border-success/30 bg-success/[0.07] px-3.5 py-3">
            <CheckCheck size={15} className="mt-[1px] flex-shrink-0 text-[hsl(var(--record-ink-success))]" />
            <div>
              <p className="text-[12.5px] font-bold text-[hsl(var(--record-ink-success))]">
                Parents can see these results
              </p>
              <p className="mt-0.5 text-[11px] leading-relaxed text-[hsl(var(--record-ink-success))]/85">
                Marks are frozen while published. To correct one, take the exam back from parents on
                the exams page first.
              </p>
            </div>
          </div>
        )}

        {(saveState.error || clearState.error) && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-destructive/25 bg-destructive/[0.06] px-3 py-2.5 text-[11.5px] font-medium text-[hsl(var(--record-ink-danger))]"
          >
            <TriangleAlert size={13} className="mt-[1px] flex-shrink-0" />
            {getApiError(saveState.error ?? clearState.error)}
          </div>
        )}

        {view === 'marks' ? (
          <>
            <div className="grid grid-cols-3 gap-3">
              <StatTile label="On the sheet" value={sheet?.onSheet ?? 0} tone="neutral" hint={subject?.name} />
              <StatTile label="Marked" value={sheet?.entered ?? 0} tone="success" />
              <StatTile
                label="Still to mark"
                value={Math.max(0, (sheet?.onSheet ?? 0) - (sheet?.entered ?? 0))}
                tone={(sheet?.onSheet ?? 0) - (sheet?.entered ?? 0) > 0 ? 'warning' : 'success'}
              />
            </div>

            <div className="overflow-clip rounded-lg border border-border bg-card">
              <table className="w-full border-collapse text-[12.5px]">
                <thead className="sticky top-0 z-10 bg-card">
                  <tr className="border-b border-border">
                    <th className={cn(styles.label, 'px-3 py-2 text-left')}>Student</th>
                    <th className={cn(styles.label, 'w-[110px] px-3 py-2 text-right')}>Class /{classMax}</th>
                    <th className={cn(styles.label, 'w-[110px] px-3 py-2 text-right')}>Exam /{examMax}</th>
                    <th className={cn(styles.label, 'w-[90px] px-3 py-2 text-right')}>Total</th>
                    <th className="w-[40px] px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {isFetching && !sheet ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-8 text-center">
                        <Spinner />
                      </td>
                    </tr>
                  ) : (sheet?.rows.length ?? 0) === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-8 text-center text-[11.5px] text-muted-foreground">
                        No active students in this class.
                      </td>
                    </tr>
                  ) : (
                    sheet!.rows.map((r) => {
                      const total = totalFor(r.studentId);
                      const edited = draft[r.studentId] !== undefined;
                      return (
                        <tr
                          key={r.studentId}
                          className={cn('border-b border-border last:border-b-0', edited && 'bg-primary/[0.05]')}
                        >
                          <td className="px-3 py-1.5">
                            <span className="font-semibold text-foreground">{r.fullName}</span>
                            {r.unmarked && !edited && (
                              <Mark tone="warning" className="ml-2">
                                Not marked
                              </Mark>
                            )}
                          </td>
                          <td className="px-3 py-1.5 text-right">
                            <input
                              inputMode="numeric"
                              aria-label={`Class score for ${r.fullName}`}
                              disabled={published}
                              value={valueFor(r.studentId, 'classScore')}
                              onChange={(e) => setValue(r.studentId, 'classScore', e.target.value)}
                              className="w-[64px] rounded-md border border-border bg-background px-2 py-1 text-right tabular-nums text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                            />
                          </td>
                          <td className="px-3 py-1.5 text-right">
                            <input
                              inputMode="numeric"
                              aria-label={`Exam score for ${r.fullName}`}
                              disabled={published}
                              value={valueFor(r.studentId, 'examScore')}
                              onChange={(e) => setValue(r.studentId, 'examScore', e.target.value)}
                              className="w-[64px] rounded-md border border-border bg-background px-2 py-1 text-right tabular-nums text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                            />
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums">
                            {total === null ? (
                              <span className="text-muted-foreground/50">—</span>
                            ) : (
                              <span className="font-bold text-foreground">{total}</span>
                            )}
                          </td>
                          <td className="px-3 py-1.5 text-right">
                            {/* Only on a mark that is SAVED. Clearing a number
                                still being typed is what Backspace is for. */}
                            {!r.unmarked && !published && (
                              <Button
                                variant="ghost"
                                size="sm"
                                iconOnly
                                aria-label={`Clear ${r.fullName}'s mark`}
                                title={`Clear ${r.fullName}'s mark — back to not marked, not to zero`}
                                loading={
                                  clearState.isLoading &&
                                  (clearState.originalArgs as { studentId?: string } | undefined)?.studentId ===
                                    r.studentId
                                }
                                onClick={() => {
                                  setDraft((d) => {
                                    const next = { ...d };
                                    delete next[r.studentId];
                                    return next;
                                  });
                                  void clearScore({ institutionId, examId, subjectId, studentId: r.studentId });
                                }}
                              >
                                <X size={13} />
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>

              {!published && (
                <div className="flex items-center gap-3 border-t border-border px-3 py-2.5">
                  <p className="text-[11px] text-muted-foreground">
                    {complete.length === 0
                      ? `Type a class score out of ${classMax} and an exam score out of ${examMax} for a child.`
                      : `${complete.length} ready to save`}
                    {partial > 0 && (
                      <span className="ml-1 text-[hsl(var(--record-ink-warning))]">
                        · {partial} half-typed, which will not be saved
                      </span>
                    )}
                  </p>
                  <Button
                    variant="primary"
                    size="sm"
                    className="ml-auto"
                    loading={saveState.isLoading}
                    disabled={complete.length === 0}
                    onClick={() => void save()}
                  >
                    <Save size={13} className="mr-1" />
                    Save marks
                  </Button>
                </div>
              )}
            </div>
          </>
        ) : (
          /* ── Ranking ─────────────────────────────────────────────────── */
          <div className="overflow-clip rounded-lg border border-border bg-card">
            <table className="w-full border-collapse text-[12.5px]">
              <thead className="sticky top-0 z-10 bg-card">
                <tr className="border-b border-border">
                  <th className={cn(styles.label, 'w-[60px] px-3 py-2 text-left')}>Pos</th>
                  <th className={cn(styles.label, 'px-3 py-2 text-left')}>Student</th>
                  <th className={cn(styles.label, 'w-[90px] px-3 py-2 text-right')}>Subjects</th>
                  <th className={cn(styles.label, 'w-[90px] px-3 py-2 text-right')}>Total</th>
                  <th className={cn(styles.label, 'w-[90px] px-3 py-2 text-right')}>Average</th>
                  <th className={cn(styles.label, 'w-[80px] px-3 py-2 text-right')}>Grade</th>
                  <th className={cn(styles.label, 'w-[88px] px-3 py-2 text-left')}>Remarks</th>
                  <th className="w-[44px] px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {rankingLoading && ranking.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-8 text-center">
                      <Spinner />
                    </td>
                  </tr>
                ) : ranking.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-8 text-center text-[11.5px] text-muted-foreground">
                      <ListOrdered size={18} className="mx-auto mb-1.5 opacity-40" />
                      No marks in this class yet.
                    </td>
                  </tr>
                ) : (
                  ranking.map((c) => (
                    <tr key={c.studentId} className="border-b border-border last:border-b-0">
                      <td className="px-3 py-2 tabular-nums font-bold text-foreground">
                        {c.subjectsTaken > 0 ? c.position : '—'}
                      </td>
                      <td className="px-3 py-2">
                        <span className="font-semibold text-foreground">{c.studentName}</span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{c.subjectsTaken}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{c.totalScore}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-bold text-foreground">{c.average}</td>
                      <td className="px-3 py-2 text-right">
                        <Mark tone={c.average >= 50 ? 'success' : c.subjectsTaken === 0 ? 'neutral' : 'warning'}>
                          {c.overallGrade}
                        </Mark>
                      </td>
                      <td className="px-3 py-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openRemark(c)}
                          aria-label={`Write a remark for ${c.studentName}`}
                          title={`Write a remark for ${c.studentName}`}
                        >
                          <MessageSquare size={12} className="mr-1" />
                          {c.classTeacherRemark || c.headTeacherRemark ? 'Written' : 'Add'}
                        </Button>
                      </td>
                      <td className="px-3 py-2 text-right">
                        {c.subjectsTaken > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            iconOnly
                            aria-label={`Print ${c.studentName}'s report card`}
                            title={`Print ${c.studentName}'s report card`}
                            onClick={() =>
                              navigate(`/exams/${examId}/print?groupId=${groupId}&studentId=${c.studentId}`)
                            }
                          >
                            <Printer size={13} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/*
        One child, two remarks, written by two different people about two
        different things — the class teacher on how the child worked, the head
        teacher signing the result off.
      */}
      <Modal
        open={remarkFor !== null}
        onClose={() => setRemarkFor(null)}
        title={remarkFor ? `Remarks for ${remarkFor.studentName}` : 'Remarks'}
        description={
          remarkFor
            ? `${remarkFor.studentClass} · average ${remarkFor.average} · ${remarkFor.position > 0 ? `${remarkFor.position} of ${remarkFor.classSize}` : 'not ranked'}`
            : undefined
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setRemarkFor(null)}>
              Cancel
            </Button>
            <Button variant="primary" loading={remarkState.isLoading} onClick={() => void submitRemark()}>
              Save remark
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Field
            label="Class teacher's remark"
            htmlFor="remark-class"
            hint="How this child worked this term. Written by whoever teaches them."
          >
            <textarea
              id="remark-class"
              rows={3}
              maxLength={400}
              value={remarkDraft.classTeacherRemark}
              onChange={(e) => setRemarkDraft({ ...remarkDraft, classTeacherRemark: e.target.value })}
              placeholder="Has worked hard at his reading this term."
              className="w-full rounded-md border border-border bg-background px-2.5 py-2 text-[12.5px] leading-relaxed text-foreground placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </Field>

          <Field
            label="Head teacher's remark"
            htmlFor="remark-head"
            hint={
              isOffice
                ? 'Signing the result off. Printed under the class teacher’s.'
                : 'Written by the office. You can read it here but not change it.'
            }
          >
            <textarea
              id="remark-head"
              rows={3}
              maxLength={400}
              disabled={!isOffice}
              value={remarkDraft.headTeacherRemark}
              onChange={(e) => setRemarkDraft({ ...remarkDraft, headTeacherRemark: e.target.value })}
              placeholder={isOffice ? 'A pleasing term. Promoted to Class 2.' : ''}
              className="w-full rounded-md border border-border bg-background px-2.5 py-2 text-[12.5px] leading-relaxed text-foreground placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
            />
          </Field>

          {remarkState.error && (
            <p role="alert" className="text-[11.5px] font-medium text-[hsl(var(--record-ink-danger))]">
              {getApiError(remarkState.error)}
            </p>
          )}

          {/* Left blank on purpose is a valid choice — the printed card keeps a
              ruled line so it can still be written in by hand. */}
          <p className={styles.meta}>
            Anything left blank prints as a ruled line to write on.
          </p>
        </div>
      </Modal>
    </PageLayout>
  );
}
